import type { UserInfo } from '@vben/types'

import type { AuthApi } from '#/api'

import { LOGIN_PATH } from '@vben/constants'
import { preferences } from '@vben/preferences'
import { resetAllStores, useAccessStore, useUserStore } from '@vben/stores'

import { notification } from 'ant-design-vue'
import { defineStore } from 'pinia'

import { authApi, userApi } from '#/api'
import { $t } from '#/locales'
import { getMicroApp, isInMicroApp } from '#/micro-app'
import { go } from '#/router'

export const useAuthStore = defineStore('auth', () => {
  const accessStore = useAccessStore()
  const userStore = useUserStore()
  const router = useRouter()

  const loginLoading = ref(false)

  /**
   * 异步处理登录操作
   * Asynchronously handle the login process
   * @param params 登录表单数据
   */
  async function authLogin(
    params: AuthApi.LoginParams,
    onSuccess?: () => Promise<void> | void,
  ) {
    // 异步处理用户登录操作并获取 accessToken
    let userInfo: null | UserInfo = null
    try {
      loginLoading.value = true
      const { accessToken } = await authApi.login(params)

      console.log('accessToken', accessToken)

      // 如果成功获取到 accessToken
      if (accessToken) {
        accessStore.setAccessToken(accessToken)

        // 获取用户信息并存储到 accessStore 中
        const [fetchUserInfoResult, accessCodes] = await Promise.all([
          fetchUserInfo(),
          authApi.getAccessCodes(),
        ])

        userInfo = fetchUserInfoResult

        userStore.setUserInfo(userInfo)
        accessStore.setAccessCodes(accessCodes.authCodes)

        if (accessStore.loginExpired) {
          accessStore.setLoginExpired(false)
        } else {
          console.log('onSuccess', onSuccess)
          onSuccess
            ? await onSuccess?.()
            : await go(userInfo?.homePath || preferences.app.defaultHomePath)
        }

        if (userInfo?.realName) {
          notification.success({
            description: `${$t('authentication.loginSuccessDesc')}:${userInfo?.realName}`,
            duration: 3,
            message: $t('authentication.loginSuccess'),
          })
        }
      }
    } finally {
      loginLoading.value = false
    }

    return {
      userInfo,
    }
  }

  async function logout(redirect: boolean = true) {
    try {
      if (isInMicroApp()) {
        getMicroApp().logout()
      } else {
        await authApi.logout()
      }
    } catch {
      // 不做任何处理
    }
    resetAllStores()
    accessStore.setLoginExpired(false)

    // 回登录页带上当前路由地址
    await router.replace({
      path: LOGIN_PATH,
      query: redirect
        ? {
            redirect: encodeURIComponent(router.currentRoute.value.fullPath),
          }
        : {},
    })
  }

  async function fetchUserInfo() {
    let userInfo: null | UserInfo = null
    userInfo = isInMicroApp()
      ? ((await getMicroApp().getGlobalData('userInfo')) as any)
      : await userApi.getUserInfo()
    userStore.setUserInfo(userInfo)
    return userInfo
  }

  function $reset() {
    loginLoading.value = false
  }

  return {
    $reset,
    authLogin,
    fetchUserInfo,
    loginLoading,
    logout,
  }
})
